import { Link } from "react-router-dom";
import { Info, MessageCircle, ArrowLeft } from "lucide-react";
import { SITE, MENU_CATEGORIES } from "../data/site";
import { SectionHead, VertKanji } from "../components/Chrome";

function MenuSheet({ category, index }) {
  return (
    <article
      data-testid={`menu-category-${category.id}`}
      className="relative border-2 border-ink bg-white p-7 shadow-[8px_8px_0_#E4DFD3] sm:p-9"
    >
      <span aria-hidden="true" className="absolute right-5 top-5 font-display text-3xl font-bold text-salmon/25">
        {String(index + 1).padStart(2, "0")}
      </span>
      <p className="text-xs font-bold tracking-[0.35em] text-soy">{category.jp}</p>
      <h2 className="mt-2 font-display text-2xl font-bold text-ink">{category.title}</h2>
      <p className="mt-1.5 text-sm text-sumi">{category.desc}</p>
      {category.items.length > 0 ? (
        <ul className="mt-6 space-y-5">
          {category.items.map((item) => (
            <li key={item.name} data-testid={`menu-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="flex items-baseline gap-3">
                <h3 className="font-display text-base font-bold text-ink">{item.name}</h3>
                <span aria-hidden="true" className="flex-1 -translate-y-1 border-b border-dotted border-ink/30" />
                {item.tag ? (
                  <span className="shrink-0 border border-hanko/40 bg-hanko/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-hanko">
                    {item.tag}
                  </span>
                ) : null}
              </div>
              <p className="mt-1 text-sm leading-relaxed text-sumi">{item.desc}</p>
            </li>
          ))}
        </ul>
      ) : null}
      {category.footnote ? (
        <p className="mt-6 border-t border-dotted border-ink/20 pt-4 text-xs italic text-sumi">{category.footnote}</p>
      ) : null}
    </article>
  );
}

export default function MenuPage() {
  return (
    <>
      <section className="relative overflow-hidden border-b-2 border-ink bg-ink" data-testid="menu-hero">
        <img
          src="/images/sushi-boat.jpg"
          alt="Aneka sushi dan sashimi tersaji di atas wadah kayu"
          width="1600"
          height="900"
          fetchPriority="high"
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
          <nav aria-label="Breadcrumb" className="text-xs font-semibold uppercase tracking-[0.25em] text-paper/60">
            <Link to="/" data-testid="breadcrumb-home" className="transition-colors hover:text-paper">
              Beranda
            </Link>
            <span aria-hidden="true"> / </span>
            <span className="text-salmon">Menu</span>
          </nav>
          <div className="mt-6 flex items-start justify-between gap-6">
            <h1 className="font-display text-4xl font-bold tracking-tight text-paper sm:text-5xl">
              Menu Umaku Sushi
            </h1>
            <VertKanji text="お品書き" className="hidden text-2xl font-bold text-salmon/70 sm:block" />
          </div>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-paper/80">
            Dari sashimi potongan tebal sampai roll fusion dan ramen — semuanya disiapkan dari bahan segar dan
            100% halal MUI.
          </p>
        </div>
      </section>

      <section className="bg-paper py-14 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <div
            data-testid="menu-note"
            className="mb-12 flex items-start gap-4 border-2 border-soy/40 bg-parchment p-6"
          >
            <Info className="mt-0.5 h-5 w-5 shrink-0 text-soy" aria-hidden="true" />
            <p className="text-sm leading-relaxed text-sumi">
              Harga dan ketersediaan menu dapat berubah sewaktu-waktu. Untuk daftar menu terlengkap dan terbaru,
              kunjungi Instagram{" "}
              <a
                href={SITE.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="menu-instagram-link"
                className="font-bold text-hanko underline underline-offset-4"
              >
                {SITE.instagramHandle}
              </a>{" "}
              atau cek aplikasi pesan-antar seperti GrabFood dan ShopeeFood.
            </p>
          </div>
          <div className="grid gap-8 lg:grid-cols-2">
            {MENU_CATEGORIES.map((category, i) => (
              <MenuSheet key={category.id} category={category} index={i} />
            ))}
          </div>
          <div className="mt-14 flex flex-col items-start justify-between gap-6 border-2 border-ink bg-ink p-8 text-paper sm:flex-row sm:items-center">
            <div>
              <h2 className="font-display text-xl font-bold">Bingung pilih yang mana?</h2>
              <p className="mt-1 text-sm text-paper/70">
                Mulai dari Sake Salmon Sashimi — favorit pelanggan dengan potongan tebal khas Umaku.
              </p>
            </div>
            <a
              href={SITE.whatsappBintaro}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="menu-whatsapp-cta"
              className="inline-flex items-center gap-2 bg-hanko px-6 py-3.5 text-sm font-bold text-paper transition-colors hover:bg-[#a82719]"
            >
              <MessageCircle className="h-4 w-4" aria-hidden="true" />
              Pesan Sekarang
            </a>
          </div>
        </div>
      </section>
    </>
  );
}

export function MenuPageHeader() {
  return <SectionHead overline="Menu" title="Menu Umaku Sushi" />;
}

export { ArrowLeft };
